import '@elisa-luukku-sites/cms-templates/index.content-blocks.css';
import Templates from '@elisa-luukku-sites/cms-templates';
import { Component } from 'react';
import { useSessionExpiredChecker } from '../../common/hooks/useSessionExpiredChecker.js';
import { throwError } from '../../common/utils/errorUtils.js';
import { CategoryList } from '../../content-blocks/CategoryList/CategoryList.js';
import { CustomForm } from '../../content-blocks/CustomForm/CustomForm.js';
import { PromotionBlockContainer } from '../../content-blocks/PromotionBlock/PromotionBlockContainer.js';
import { BreadCrumbs, getBreadCrumbLinks } from '../BreadCrumbs/index.js';
import { NotificationContainer } from '../NotificationContainer/NotificationContainer.js';
import { ProductDetailsPublicLoader } from '../ProductDetails/ProductDetailsPublicLoader.js';
import type { ContentBlockProps, PublicPageProps } from './types.js';
import type { CategoryListData, CustomFormData, PromotionBlockData } from '../../content-blocks/cmsSchema.js';
import type { OnlineModel, OnlineModelHeader } from '../../generated/api/models.js';
import type { ProductDetailsPublicLoaderProps } from '../ProductDetails/ProductDetailsPublicLoader.js';
import type { ContactGridProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/ContactGrid/types.js';
import type { DescriptionBlockProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/DescriptionBlock/types.js';
import type { FluidHeroProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/FluidHero/types.js';
import type { FreeHTMLProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/FreeHTML/types.js';
import type { IconGridProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/IconGrid/types.js';
import type { MarketingBlockProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/MarketingBlock/types.js';
import type { MarketingSquaresProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/MarketingSquares/types.js';
import type { MultiColumnTextContainerProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/MultiColumnTextContainer/types.js';
import type { OfferCardsProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/OfferCards/types.js';
import type { ProductHeroData } from '@elisa-luukku-sites/cms-templates/types/src/templates/ProductHero/types.js';
import type { TableLayoutProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/TableLayout/types.js';
import type { TextBoxContainerProps } from '@elisa-luukku-sites/cms-templates/types/src/templates/TextboxContainer/types.js';
import type { ErrorInfo, JSX, ReactNode } from 'react';

import './PublicPage.scss';

const SiteTemplates = Templates.default;

const getOnlineModelById = (id: string, onlineModels: OnlineModel[]) => {
  const onlineModel = onlineModels.find(om => id === om.onlineModelCode);
  if (onlineModel) {
    return onlineModel;
  }
  return throwError(`Unable to load onlineModel for the id: ${id}`);
};

const getOnlineModelHeadersByTagName = (
  categoryList: CategoryListData,
  onlineModelHeaders: OnlineModelHeader[]
): OnlineModelHeader[] => {
  // using toLowerCase() to match e.g. CategoryLists's 'Tablet' tag name to Online Model's 'TABLET' category name
  const requestedTags = (categoryList.productsWithTags || []).map(tag => tag.toLowerCase());
  return onlineModelHeaders.filter(model => {
    // TODO: Ideally, backend would return `model.tags` populated with both Salesforce and
    // Contentful tags. Due to limitations in data model and current backend implementation, this
    // workaround combines Salesforce's `model.category` and Contentful's `model.tags` on the
    // frontend side. The backend should be corrected and this code removed.
    const modelTags = [model.category, ...(model.tags || [])].map(tag => tag.toLowerCase());

    // spec: model must contain all requested tags to be included in result
    return requestedTags.every(i => modelTags.includes(i));
  });
};

type ContentBlockType =
  | 'accordion'
  | 'contactGrid'
  | 'descriptionBlock'
  | 'fluidHero'
  | 'freeHTML'
  | 'iconGrid'
  | 'marketingBlock'
  | 'marketingSquares'
  | 'multiColumnTextContainer'
  | 'offerCards'
  | 'productHero'
  | 'promotionBlock'
  | 'textboxContainer'
  | 'categoryList'
  | 'customForm'
  | 'productDetails'
  | 'tableLayout';

type ContentBlockContent = (props: React.PropsWithChildren<ContentBlockProps>) => JSX.Element | null;

const contentBlockComponents: { [T in ContentBlockType]: ContentBlockContent } = {
  accordion: ({ content }) => <SiteTemplates.Accordion {...content} />,
  categoryList: ({ content, onlineModelHeaders, onAnalyticsEvent, eCommerceEvents, ...rest }) => (
    <CategoryList
      categoryListData={{ ...(content as CategoryListData), onAnalyticsEvent, eCommerceEvents }}
      {...rest}
      onlineModelHeaders={getOnlineModelHeadersByTagName(content as CategoryListData, onlineModelHeaders)}
    />
  ),
  contactGrid: ({ content }) => <SiteTemplates.ContactGrid {...(content as ContactGridProps)} />,
  customForm: ({ content, sitesId, ...rest }) => (
    <CustomForm {...({ ...content, id: sitesId } as CustomFormData)} {...rest} />
  ),
  descriptionBlock: ({ content }) => <SiteTemplates.DescriptionBlock {...(content as DescriptionBlockProps)} />,
  fluidHero: ({ content }) => <SiteTemplates.FluidHero {...(content as FluidHeroProps)} />,
  freeHTML: ({ content }) => <SiteTemplates.FreeHTML {...(content as FreeHTMLProps)} />,
  iconGrid: ({ content }) => <SiteTemplates.IconGrid {...(content as IconGridProps)} />,
  marketingBlock: ({ content }) => <SiteTemplates.MarketingBlock {...(content as MarketingBlockProps)} />,
  marketingSquares: ({ content }) => <SiteTemplates.MarketingSquares {...(content as MarketingSquaresProps)} />,
  multiColumnTextContainer: ({ content }) => (
    <SiteTemplates.MultiColumnTextContainer {...(content as MultiColumnTextContainerProps)} />
  ),
  offerCards: ({ content }) => <SiteTemplates.OfferCards {...(content as OfferCardsProps)} />,
  productDetails: ({ content, onlineModels, ...rest }) => {
    const data = content as ProductDetailsPublicLoaderProps;
    const onlineModel = getOnlineModelById(data.productGuid, onlineModels);
    if (!onlineModel) {
      return null;
    }
    return <ProductDetailsPublicLoader {...data} {...rest} onlineModel={onlineModel} />;
  },
  productHero: ({ content }) => <SiteTemplates.ProductHero {...(content as ProductHeroData)} />,
  promotionBlock: ({ content, onlineModelHeaders, ...rest }) => {
    return (
      <PromotionBlockContainer {...(content as PromotionBlockData)} {...rest} onlineModelHeaders={onlineModelHeaders} />
    );
  },
  textboxContainer: ({ content }) => <SiteTemplates.TextboxContainer {...(content as TextBoxContainerProps)} />,
  tableLayout: ({ content }) => <SiteTemplates.TableLayout {...(content as TableLayoutProps)} />,
};

const ContentBlock = (props: ContentBlockProps) => {
  const contentBlockTypeLowerCase = props.contentBlockType.toLowerCase();
  for (const [name, LocalComponent] of Object.entries(contentBlockComponents)) {
    if (`cb-${name.toLowerCase()}` === contentBlockTypeLowerCase) {
      return <LocalComponent {...props} />;
    }
  }
  return null;
};

type ContentBlockErrorBoundaryProps = {
  pageId: string;
  contentBlockId: string;
  contentBlockType: string;
  children: ReactNode;
};

type ContentBlockErrorBoundaryState = {
  hasError: boolean;
};

class ContentBlockErrorBoundary extends Component<ContentBlockErrorBoundaryProps, ContentBlockErrorBoundaryState> {
  override state: ContentBlockErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ContentBlockErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { pageId, contentBlockId, contentBlockType } = this.props;
    // eslint-disable-next-line no-console
    console.error(`[PublicPage] Failed to render content block `, {
      pageId,
      contentBlockId,
      contentBlockType,
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  override render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export const PublicPage = (props: PublicPageProps) => {
  useSessionExpiredChecker();
  return (
    <>
      <div className="page site cms-page of-public-page" data-page-id={props.pageId}>
        <NotificationContainer />
        <BreadCrumbs breadCrumbPaths={getBreadCrumbLinks(props.breadcrumbs, props.siteStructureNodes)} />
        {props.children}
        {props.contentBlocks.map((contentBlockProps, index) => {
          const { contentBlockType, fields, contentBlockId } = contentBlockProps;
          const id = 'id' in fields && typeof fields.id === 'string' ? fields.id : 'block-' + contentBlockId;
          return (
            <ContentBlockErrorBoundary
              key={`of-content-block-${contentBlockId}-${index}`}
              pageId={props.pageId}
              contentBlockId={contentBlockId}
              contentBlockType={contentBlockType}
            >
              <div
                id={id}
                key={`of-content-block-${contentBlockId}-${index}`}
                data-content-block-id={contentBlockId}
                data-content-block-type={contentBlockType}
              >
                <ContentBlock
                  sitesId={id}
                  contentBlockType={contentBlockType}
                  content={{ ...fields, id: `content-${id}` }}
                  {...props}
                />
              </div>
            </ContentBlockErrorBoundary>
          );
        })}
      </div>
    </>
  );
};
